import React, { useState } from "react";
import PropTypes from "prop-types";
import { Grid, Typography } from "@material-ui/core";
import moment from "moment";
import Modal from "../../../app/components/modals/Modal";
import useStyles from "./styles";
import { translate as t } from "../../translate/localization";
import CobeeButton from "../../../app/components/CobeeButton";
import { hideModal } from "../../../usecases/commons/hide-modal";
import RowPurchasePrice from "./components/RowPurchasePrice";
import RowPrivateUse from "./components/RowPrivateUse";
import RowEnergeticEfficiency from "./components/RowEnergeticEfficiency";
import RowInitialPrice from "./components/RowInitialPrice";
import RowWhoPaysQuotas from "./components/RowWhoPaysQuotas";
import RowCustomQuota from "./components/RowCustomQuota";
import RowWhoPaysVat from "./components/RowWhoPaysVat";
import VatCompanyMessage from "./components/VatCompanyMessage";
import RowDate from "./components/RowDate";
import RowNumberQuotas from "./components/RowNumberQuotas";

export default function ApproveRentingModal({
  callback,
  defaultValues,
  isLoading,
}) {
  const { container, row, buttonRow, fieldRowSpacing } = useStyles();

  const [privateUsePercentage, setPrivateUsePercentage] = useState(
    defaultValues.privateUsePercentage
  );
  const [energeticEfficiency, setEnergeticEfficiency] = useState(
    defaultValues.energeticEfficiency
  );

  const [purchasePrice, setPurchasePrice] = useState(
    defaultValues.purchasePrice
  );
  const [initialAmount, setInitialAmount] = useState(
    defaultValues.initialAmount
  );

  const [rentingQuota, setRentingQuota] = useState(defaultValues.rentingQuota);
  const [rentingCompanyQuota, setRentingCompanyQuota] = useState(
    defaultValues.rentingCompanyQuota
  );
  const [rentingEmployeeQuota, setRentingEmployeeQuota] = useState(
    defaultValues.rentingEmployeeQuota
  );

  const [date, setDate] = useState(defaultValues.date);
  const [numberOfQuotas, setNumberOfQuotas] = useState(
    defaultValues.numberOfQuotas
  );

  const showAloneRowQuota = () => defaultValues.whoPaysQuotas !== "both";

  const showEmployeeRowVat = () => defaultValues.whoPaysQuotas !== "company";

  const checkRentingQuota = () => {
    const { whoPaysQuotas } = defaultValues;
    if (whoPaysQuotas === "both") {
      return (
        rentingCompanyQuota <= 0 ||
        rentingCompanyQuota === "" ||
        rentingEmployeeQuota <= 0 ||
        rentingEmployeeQuota === ""
      );
    }
    return rentingQuota <= 0 || rentingQuota === "";
  };

  const shouldDisabledResultsButton = () => {
    return numberOfQuotas < 2 || checkRentingQuota() || purchasePrice <= 0;
  };

  return (
    <Modal title={t("actions.approve-request")}>
      <Grid item xs={12} container justify="center" className={container}>
        <Grid item container xs={12} className={row}>
          <Grid item container xs={12}>
            <Typography variant="h6">
              {t("request-section.information-for-tax-calculation")}
            </Typography>
          </Grid>
          <RowPurchasePrice
            purchasePrice={purchasePrice}
            setPurchasePrice={setPurchasePrice}
          />
          <RowPrivateUse
            privateUsePercentage={privateUsePercentage}
            setPrivateUsePercentage={setPrivateUsePercentage}
          />
          <RowEnergeticEfficiency
            energeticEfficiency={energeticEfficiency}
            setEnergeticEfficiency={setEnergeticEfficiency}
          />
        </Grid>
        <Grid item container xs={12} className={row}>
          <Grid item container xs={12} className={fieldRowSpacing}>
            <Typography variant="h6">
              {t("request-section.collections-and-renting-quotas")}
            </Typography>
          </Grid>
          <RowInitialPrice
            initialAmount={initialAmount}
            setInitialAmount={setInitialAmount}
          />
          <RowWhoPaysQuotas
            defaultValues={defaultValues}
            callback={callback}
            privateUsePercentage={privateUsePercentage}
            energeticEfficiency={energeticEfficiency}
            purchasePrice={purchasePrice}
            initialAmount={initialAmount}
            rentingQuota={rentingQuota}
            rentingCompanyQuota={rentingCompanyQuota}
            rentingEmployeeQuota={rentingEmployeeQuota}
            date={date}
            numberOfQuotas={numberOfQuotas}
          />
          {showAloneRowQuota() ? (
            <RowCustomQuota
              renting={rentingQuota}
              setRenting={setRentingQuota}
              title={t("request-section.amount-quota")}
              label={t("common-messages.tax-not-included")}
            />
          ) : (
            <>
              <RowCustomQuota
                renting={rentingCompanyQuota}
                setRenting={setRentingCompanyQuota}
                title={t("request-section.amount-quota-paid-company")}
                label={t("common-messages.tax-not-included")}
              />
              <RowCustomQuota
                renting={rentingEmployeeQuota}
                setRenting={setRentingEmployeeQuota}
                title={t("request-section.amount-quota-paid-employee")}
                label={t("common-messages.tax-not-included")}
              />
            </>
          )}
          {showEmployeeRowVat() ? (
            <RowWhoPaysVat
              defaultValues={defaultValues}
              callback={callback}
              privateUsePercentage={privateUsePercentage}
              energeticEfficiency={energeticEfficiency}
              purchasePrice={purchasePrice}
              initialAmount={initialAmount}
              rentingQuota={rentingQuota}
              rentingCompanyQuota={rentingCompanyQuota}
              rentingEmployeeQuota={rentingEmployeeQuota}
              date={date}
              numberOfQuotas={numberOfQuotas}
            />
          ) : (
            <VatCompanyMessage />
          )}
          <RowDate date={date} setDate={setDate} />
          <RowNumberQuotas
            numberOfQuotas={numberOfQuotas}
            setNumberOfQuotas={setNumberOfQuotas}
          />
        </Grid>
        <Grid
          item
          container
          xs={12}
          justify="center"
          alignItems="center"
          className={buttonRow}
        >
          <CobeeButton styleType="secondary" onClick={hideModal}>
            {t("actions.cancel")}
          </CobeeButton>
          <CobeeButton
            styleType="primary"
            disabled={shouldDisabledResultsButton()}
            isLoading={isLoading}
            onClick={() =>
              callback({
                privateUsePercentage,
                energeticEfficiency,
                purchasePrice,
                initialAmount,
                rentingQuota,
                rentingEmployeeQuota,
                rentingCompanyQuota,
                date,
                numberOfQuotas,
                whoPaysQuotas: defaultValues.whoPaysQuotas,
                whoPaysVat: defaultValues.whoPaysVat,
              })
            }
          >
            {t("actions.watch-results")}
          </CobeeButton>
        </Grid>
      </Grid>
    </Modal>
  );
}

ApproveRentingModal.defaultProps = {
  defaultValues: {
    privateUsePercentage: 0,
    energeticEfficiency: 0,
    purchasePrice: 0,
    initialAmount: 0,
    rentingQuota: 0,
    rentingCompanyQuota: 0,
    rentingEmployeeQuota: 0,
    date: new Date(moment().add(1, "months").startOf("month")),
    numberOfQuotas: 0,
    whoPaysQuotas: "employee",
    whoPaysVat: "employee",
  },
  callback: () => {},
  isLoading: false,
};

ApproveRentingModal.propTypes = {
  defaultValues: PropTypes.object,
  callback: PropTypes.func,
  isLoading: PropTypes.bool,
};
